// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import {
  useAccrualPolicies,
  useCreateAccrualPolicy,
  useUpdateAccrualPolicy,
  useDeleteAccrualPolicy,
} from '../../hooks/useAccrualPolicies';

interface AccrualMilestone {
  yearsOfService: number;
  annualAccrualHours: number;
}

interface AccrualPolicy {
  [key: string]: unknown;
  id: string;
  originalId: string;
  version: number;
  isActive: boolean;
  name: string;
  policyType: string;
  accrualFrequency: string;
  maxBalanceHours: number;
  carryoverMaxHours: number;
  waitingPeriodDays: number;
  milestones: AccrualMilestone[];
  employeeCount: number;
  status: string;
  createdAt: string;
}

interface PolicyFormValues {
  name: string;
  policyType: string;
  accrualFrequency: string;
  maxBalanceHours: number;
  carryoverMaxHours: number;
  waitingPeriodDays: number;
  milestones: AccrualMilestone[];
}

const POLICY_TYPES = [
  { value: 'calendar', label: 'Calendar-based' },
  { value: 'anniversary', label: 'Anniversary-based' },
  { value: 'manual', label: 'Manual' },
];

const ACCRUAL_FREQUENCIES = [
  { value: 'per_pay_period', label: 'Per Pay Period' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const STATUS_COLOR_MAP: Record<string, string> = {
  active: 'green',
  inactive: 'gray',
  draft: 'yellow',
};

function emptyFormValues(): PolicyFormValues {
  return {
    name: '',
    policyType: 'calendar',
    accrualFrequency: 'per_pay_period',
    maxBalanceHours: 0,
    carryoverMaxHours: 0,
    waitingPeriodDays: 0,
    milestones: [{ yearsOfService: 1, annualAccrualHours: 40 }],
  };
}

export default function AccrualPolicyPage() {
  const [page, setPage] = useState(1);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<AccrualPolicy | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const { data, loading: isLoading } = useAccrualPolicies({ page, perPage: 25 });
  const createMutation = useCreateAccrualPolicy();
  const updateMutation = useUpdateAccrualPolicy();
  const deleteMutation = useDeleteAccrualPolicy();

  const form = useForm<PolicyFormValues>({
    initialValues: emptyFormValues(),
    validate: {
      name: (value) => (value.trim().length === 0 ? 'Name is required' : null),
      policyType: (value) => (!value ? 'Policy type is required' : null),
      accrualFrequency: (value) => (!value ? 'Accrual frequency is required' : null),
      maxBalanceHours: (value) =>
        value < 0 ? 'Max balance must be non-negative' : null,
      milestones: {
        yearsOfService: (value) =>
          value < 0 ? 'Years must be non-negative' : null,
        annualAccrualHours: (value) =>
          value <= 0 ? 'Hours must be positive' : null,
      },
    },
  });

  const handleOpenCreate = useCallback(() => {
    setEditingPolicy(null);
    form.setValues(emptyFormValues());
    setFormModalOpen(true);
  }, [form]);

  const handleOpenEdit = useCallback(
    (policy: AccrualPolicy) => {
      setEditingPolicy(policy);
      form.setValues({
        name: policy.name,
        policyType: policy.policyType,
        accrualFrequency: policy.accrualFrequency,
        maxBalanceHours: policy.maxBalanceHours,
        carryoverMaxHours: policy.carryoverMaxHours,
        waitingPeriodDays: policy.waitingPeriodDays,
        milestones:
          policy.milestones.length > 0
            ? policy.milestones
            : [{ yearsOfService: 1, annualAccrualHours: 40 }],
      });
      setFormModalOpen(true);
    },
    [form],
  );

  const handleSubmitForm = useCallback(
    (values: PolicyFormValues) => {
      const payload = {
        name: values.name.trim(),
        policyType: values.policyType,
        accrualFrequency: values.accrualFrequency,
        maxBalanceHours: values.maxBalanceHours,
        carryoverMaxHours: values.carryoverMaxHours,
        waitingPeriodDays: values.waitingPeriodDays,
        milestones: values.milestones.filter((m) => m.annualAccrualHours > 0),
      };

      if (editingPolicy) {
        updateMutation.mutate({ id: editingPolicy.id, input: payload })
          .then(() => {
            notifications.show({
              title: 'Policy Updated',
              message: `New version created for "${payload.name}".`,
              color: 'green',
            });
            setFormModalOpen(false);
          })
          .catch((error: unknown) => {
            notifications.show({
              title: 'Update Failed',
              message: error instanceof Error ? error.message : 'An error occurred.',
              color: 'red',
            });
          });
      } else {
        createMutation.mutate(payload)
          .then(() => {
            notifications.show({
              title: 'Policy Created',
              message: `"${payload.name}" has been created.`,
              color: 'green',
            });
            setFormModalOpen(false);
          })
          .catch((error: unknown) => {
            notifications.show({
              title: 'Creation Failed',
              message: error instanceof Error ? error.message : 'An error occurred.',
              color: 'red',
            });
          });
      }
    },
    [editingPolicy, createMutation, updateMutation],
  );

  const handleOpenDelete = useCallback((id: string) => {
    setDeleteTargetId(id);
    setDeleteReason('');
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTargetId || !deleteReason.trim()) return;
    deleteMutation.mutate({ id: deleteTargetId })
      .then(() => {
        notifications.show({
          title: 'Policy Deactivated',
          message: 'Policy has been soft-deleted with a new version.',
          color: 'green',
        });
        setDeleteModalOpen(false);
        setDeleteTargetId(null);
        setDeleteReason('');
      })
      .catch((error: unknown) => {
        notifications.show({
          title: 'Delete Failed',
          message: error instanceof Error ? error.message : 'An error occurred.',
          color: 'red',
        });
      });
  }, [deleteTargetId, deleteReason, deleteMutation]);

  const handleAddMilestone = useCallback(() => {
    const current = form.values.milestones;
    const lastMilestone = current.length > 0 ? current[current.length - 1] : undefined;
    const lastYear = lastMilestone ? lastMilestone.yearsOfService + 1 : 1;
    form.insertListItem('milestones', {
      yearsOfService: lastYear,
      annualAccrualHours: 40,
    });
  }, [form]);

  const handleRemoveMilestone = useCallback(
    (index: number) => {
      form.removeListItem('milestones', index);
    },
    [form],
  );

  const columns: Column<AccrualPolicy>[] = [
    { key: 'name', label: 'Policy Name', sortable: true },
    {
      key: 'policyType',
      label: 'Type',
      width: 140,
      render: (row) => {
        const label = POLICY_TYPES.find((t) => t.value === row.policyType)?.label;
        return label ?? row.policyType;
      },
    },
    {
      key: 'accrualFrequency',
      label: 'Frequency',
      width: 140,
      render: (row) => {
        const label = ACCRUAL_FREQUENCIES.find((f) => f.value === row.accrualFrequency)?.label;
        return label ?? row.accrualFrequency;
      },
    },
    {
      key: 'maxBalanceHours',
      label: 'Max Balance',
      width: 110,
      render: (row) => `${row.maxBalanceHours}h`,
    },
    {
      key: 'carryoverMaxHours',
      label: 'Carryover',
      width: 100,
      render: (row) => `${row.carryoverMaxHours}h`,
    },
    {
      key: 'waitingPeriodDays',
      label: 'Wait (days)',
      width: 100,
    },
    {
      key: 'employeeCount',
      label: 'Employees',
      width: 100,
    },
    {
      key: 'version',
      label: 'Version',
      width: 80,
      render: (row) => `v${row.version}`,
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      render: (row) => <StatusBadge status={row.status} colorMap={STATUS_COLOR_MAP} />,
    },
  ];

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <Stack gap="md">
      <PageHeader
        title="Accrual Policies"
        subtitle={data ? `${data.length} policies` : undefined}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={handleOpenCreate}>
            Create Policy
          </Button>
        }
      />

      <DataTable<AccrualPolicy>
        columns={columns}
        data={(data as AccrualPolicy[]) ?? []}
        loading={isLoading}
        pagination={
          data
            ? {
                page,
                pageSize: 25,
                total: data.length,
                onPageChange: setPage,
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        emptyMessage="No accrual policies configured"
        actions={(row) => (
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={() => handleOpenEdit(row)}
            >
              <IconPencil size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleOpenDelete(row.id)}
              disabled={!row.isActive}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        )}
      />

      {/* Create / Edit Modal */}
      <Modal
        opened={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={editingPolicy ? `Edit Policy: ${editingPolicy.name}` : 'Create Accrual Policy'}
        centered
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmitForm)}>
          <Stack gap="md">
            {editingPolicy && (
              <Text size="xs" c="dimmed">
                Saving will create a new version (v{editingPolicy.version + 1}). The
                previous version is preserved in the append-only audit log.
              </Text>
            )}

            <TextInput
              label="Policy Name"
              placeholder="e.g. Standard PTO"
              required
              {...form.getInputProps('name')}
            />

            <Group grow>
              <Select
                label="Policy Type"
                data={POLICY_TYPES}
                required
                {...form.getInputProps('policyType')}
              />
              <Select
                label="Accrual Frequency"
                data={ACCRUAL_FREQUENCIES}
                required
                {...form.getInputProps('accrualFrequency')}
              />
            </Group>

            <Group grow>
              <NumberInput
                label="Max Balance (hours)"
                min={0}
                {...form.getInputProps('maxBalanceHours')}
              />
              <NumberInput
                label="Carryover Max (hours)"
                min={0}
                {...form.getInputProps('carryoverMaxHours')}
              />
              <NumberInput
                label="Waiting Period (days)"
                min={0}
                {...form.getInputProps('waitingPeriodDays')}
              />
            </Group>

            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={500} size="sm">
                  Milestones
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  icon={<IconPlus size={14} />}
                  onClick={handleAddMilestone}
                >
                  Add Milestone
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                Define annual accrual hours based on years of service.
              </Text>
              {form.values.milestones.map((_: any, index) => (
                <Group key={index} gap="sm" ta="flex-end">
                  <NumberInput
                    label={index === 0 ? 'Years of Service' : undefined}
                    placeholder="Years"
                    min={0}
                    style={{ flex: 1 }}
                    {...form.getInputProps(`milestones.${index}.yearsOfService`)}
                  />
                  <NumberInput
                    label={index === 0 ? 'Annual Accrual Hours' : undefined}
                    placeholder="Hours"
                    min={0}
                    style={{ flex: 1 }}
                    {...form.getInputProps(`milestones.${index}.annualAccrualHours`)}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => handleRemoveMilestone(index)}
                    disabled={form.values.milestones.length <= 1}
                    mb={index === 0 ? 0 : undefined}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>

            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="default"
                onClick={() => setFormModalOpen(false)}
                disabled={isMutating}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isMutating}>
                {editingPolicy ? 'Save New Version' : 'Create Policy'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Deactivate Accrual Policy"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This will soft-delete the policy by creating a new inactive version. The
            previous version is preserved in the append-only audit log. Employees
            currently assigned to this policy will stop accruing.
          </Text>
          <Textarea
            label="Reason for Deactivation"
            placeholder="Why is this policy being deactivated?"
            required
            minRows={3}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDelete}
              loading={deleteMutation.isPending}
              disabled={!deleteReason.trim()}
            >
              Deactivate Policy
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
